import React, { useCallback, useEffect, useRef, useState } from 'react';
import {Outlet } from 'react-router-dom';
import axios from 'axios';
import { redirectToLogin, sessionCheckSettings } from './sessionExpiry';

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// A 5xx, or no answer at all (network error, timeout), says the service is in trouble, not that the user is signed
// out. Those are worth another try; a 401 or any other 4xx is an answer and retrying will not change it.
const isTransient = (err) => !err.response || err.response.status >= 500;

/**
 * Asks the gateway who we are. Resolves with 'authorized', 'login' (401: the sign-in is gone), 'unreachable'
 * (still failing after the retries) or 'rejected' (any other answer).
 */
async function checkSession() {
  const delays = sessionCheckSettings.retryDelaysMs;
  for (let attempt = 0; ; attempt++) {
    try {
      await axios.get(`${process.env.REACT_APP_NOTIFICATION_SERVICE_APIGW_ENDPOINT}/user`, { withCredentials: true });
      return 'authorized';
    } catch (err) {
      if (err.response?.status === 401) return 'login';
      if (!isTransient(err)) return 'rejected';
      if (attempt >= delays.length) return 'unreachable';
      await sleep(delays[attempt]);
    }
  }
}

const screenStyle = { display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' };
const boxStyle = { textAlign: 'center', border: '1px solid #ccc', padding: '32px 40px', borderRadius: '8px', maxWidth: '420px' };
const buttonStyle = { padding: '8px 18px', fontSize: '15px', cursor: 'pointer', margin: '0 6px', borderRadius: '4px', border: '1px solid #007bff' };

const MessageScreen = ({ title, text, onRetry }) => (
  <div style={screenStyle}>
    <div style={boxStyle} role="alert">
      <h2>{title}</h2>
      <p>{text}</p>
      {onRetry && (
        <button type="button" onClick={onRetry} style={{ ...buttonStyle, backgroundColor: '#007bff', color: 'white' }}>
          Try again
        </button>
      )}
      <button type="button" onClick={() => redirectToLogin({ force: true })} style={{ ...buttonStyle, backgroundColor: 'white', color: '#007bff' }}>
        Log in again
      </button>
    </div>
  </div>
);

/**
 * The session gate around the protected pages. `loggedOut` is optional: an element to show, in place, to someone who is not signed in
 * (the base address shows the login page this way). Without it a 401 sends the browser straight to the gateway's sign-in.
 */
const ProtectedRoute = ({ loggedOut }) => {
  // loading | authorized | loggedOut | redirecting | unreachable | rejected
  const [authStatus, setAuthStatus] = useState('loading');
  const hasChecked = useRef(false);
  const mounted = useRef(true);
  const loggedOutPage = useRef(loggedOut);
  loggedOutPage.current = loggedOut;

  const verify = useCallback(async () => {
    const result = await checkSession();
    if (!mounted.current) return;
    if (result === 'login' && loggedOutPage.current) {
      // 401 where a page for the signed-out was given: show it, nothing to redirect (its button starts the sign-in)
      setAuthStatus('loggedOut');
    } else if (result === 'login') {
      // 401: the sign-in is gone. Go to the login once; if we were just there and are still refused, say so instead of looping.
      setAuthStatus(redirectToLogin() ? 'redirecting' : 'rejected');
    } else {
      setAuthStatus(result);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    if (!hasChecked.current) {
      hasChecked.current = true;
      verify();
    }
    return () => { mounted.current = false; };
  }, [verify]);

  const tryAgain = () => {
    setAuthStatus('loading');
    verify();
  };

  if (authStatus === 'loading') return <div>Verifying session...</div>;
  if (authStatus === 'loggedOut') return loggedOutPage.current;
  if (authStatus === 'redirecting') return <div>Redirecting to sign-in...</div>;
  if (authStatus === 'unreachable') {
    return <MessageScreen title="We can't reach the service right now"
      text="This is usually brief. Try again in a moment." onRetry={tryAgain} />;
  }
  if (authStatus === 'rejected') {
    return <MessageScreen title="We couldn't confirm your sign-in"
      text="Please log in again to continue." />;
  }

  // Use Outlet so it works as a wrapper for nested routes
  return <Outlet />;
};


export default ProtectedRoute;
